import { useState, useEffect } from 'react';
import { sortByName } from '../lib/sortByName';
import { useTranslation } from 'react-i18next';
import { locationHealthAPI, organizationsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import {
    Activity,
    Building2,
    MapPin,
    Users,
    TrendingUp,
    TrendingDown,
    Minus,
    AlertCircle,
    CheckCircle,
    AlertTriangle,
    Filter,
    RefreshCw,
    ChevronDown,
    ChevronUp,
    HelpCircle,
    Target,
    Loader2,
    BarChart3,
    Calendar
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../components/ui/tooltip';
import {
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
    LineChart,
    Line
} from 'recharts';

// Help tooltip component
const HelpTooltip = ({ text }) => (
    <TooltipProvider>
        <Tooltip>
            <TooltipTrigger asChild>
                <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help inline-block ml-1" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
                <p className="text-sm">{text}</p>
            </TooltipContent>
        </Tooltip>
    </TooltipProvider>
);

const HEALTH_STATUS_CONFIG = {
    healthy: {
        labelKey: 'locationHealth.statusBadge.healthy',
        color: 'bg-emerald-500',
        textColor: 'text-emerald-700',
        bgColor: 'bg-emerald-50',
        borderColor: 'border-emerald-200',
        icon: CheckCircle
    },
    needs_attention: {
        labelKey: 'locationHealth.statusBadge.needsAttention',
        color: 'bg-amber-500',
        textColor: 'text-amber-700',
        bgColor: 'bg-amber-50',
        borderColor: 'border-amber-200',
        icon: AlertTriangle
    },
    critical: {
        labelKey: 'locationHealth.statusBadge.critical',
        color: 'bg-red-500',
        textColor: 'text-red-700',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        icon: AlertCircle
    }
};

export default function LocationHealth() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({ summary: {}, locations: [] });
    const [organizations, setOrganizations] = useState([]);
    const [selectedOrg, setSelectedOrg] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [expandedLocation, setExpandedLocation] = useState(null);
    const [showThresholds, setShowThresholds] = useState(false);
    const [thresholds, setThresholds] = useState({
        review_velocity_target: 50,
        adoption_rate_target: 70,
        green_threshold: 70,
        yellow_threshold: 40
    });
    const [savingThresholds, setSavingThresholds] = useState(false);
    const [incrementalROI, setIncrementalROI] = useState(null);

    const isSuperAdmin = () => user?.role === 'super_admin';
    const isClientAdmin = () => user?.role === 'client_admin';

    useEffect(() => {
        loadInitialData();
    }, []);

    useEffect(() => {
        loadHealthData();
        loadIncrementalROI();
    }, [selectedOrg, statusFilter]);

    const loadInitialData = async () => {
        try {
            setLoading(true);
            if (isSuperAdmin()) {
                const orgsRes = await organizationsAPI.list();
                setOrganizations(sortByName(orgsRes.data));
            }
            await loadHealthData();
            await loadIncrementalROI();
        } catch (error) {
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const loadHealthData = async () => {
        try {
            const params = {};
            if (selectedOrg && selectedOrg !== '__all__') params.organization_id = selectedOrg;
            if (statusFilter && statusFilter !== '__all__') params.status_filter = statusFilter;
            
            const res = await locationHealthAPI.getAll(params);
            setData(res.data);
        } catch (error) {
            console.error('Failed to load health data:', error);
        }
    };

    const loadIncrementalROI = async () => {
        try {
            const params = {};
            if (selectedOrg && selectedOrg !== '__all__') params.organization_id = selectedOrg;
            
            const res = await locationHealthAPI.getIncrementalROI(params);
            setIncrementalROI(res.data);
        } catch (error) {
            console.error('Failed to load incremental ROI:', error);
        }
    };

    const loadThresholds = async () => {
        try {
            const orgId = selectedOrg || user?.organization_id;
            if (!orgId) return;
            
            const res = await locationHealthAPI.getHealthThresholds(orgId);
            setThresholds(res.data.health_thresholds);
        } catch (error) {
            console.error('Failed to load thresholds:', error);
        }
    };

    const saveThresholds = async () => {
        setSavingThresholds(true);
        try {
            const orgId = selectedOrg || user?.organization_id;
            await locationHealthAPI.updateHealthThresholds(orgId, thresholds);
            toast.success('Health thresholds updated!');
            setShowThresholds(false);
            loadHealthData();
        } catch (error) {
            toast.error('Failed to save thresholds');
        } finally {
            setSavingThresholds(false);
        }
    };

    const getStatusBadge = (status) => {
        const config = HEALTH_STATUS_CONFIG[status] || HEALTH_STATUS_CONFIG.needs_attention;
        const Icon = config.icon;
        return (
            <Badge className={`${config.bgColor} ${config.textColor} border ${config.borderColor}`}>
                <Icon className="w-3 h-3 mr-1" />
                {t(config.labelKey)}
            </Badge>
        );
    };

    const getTrendIcon = (current, previous) => {
        if (current > previous) return <TrendingUp className="w-4 h-4 text-emerald-500" />;
        if (current < previous) return <TrendingDown className="w-4 h-4 text-red-500" />;
        return <Minus className="w-4 h-4 text-muted-foreground" />;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="location-health">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                        <Activity className="w-7 h-7 text-emerald-500" />
                        {t('locationHealth.title')}
                        <HelpTooltip text={t('locationHealth.titleHint')} />
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        {t('locationHealth.titleHint')}
                    </p>
                </div>
                
                <div className="flex items-center gap-3">
                    {(isSuperAdmin() || isClientAdmin()) && (
                        <Button variant="outline" onClick={() => { loadThresholds(); setShowThresholds(true); }}>
                            <Target className="w-4 h-4 mr-2" />
                            {t('locationHealth.setTargets')}
                        </Button>
                    )}
                    <Button variant="outline" onClick={loadHealthData}>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        {t('locationHealth.refresh')}
                    </Button>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="border-l-4 border-l-emerald-500">
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">{t('locationHealth.healthy')}</p>
                                <p className="text-3xl font-bold text-emerald-600">{data.summary.healthy || 0}</p>
                            </div>
                            <CheckCircle className="w-10 h-10 text-emerald-200" />
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">{t('locationHealth.healthyHint')}</p>
                    </CardContent>
                </Card>

                <Card className="border-l-4 border-l-amber-500">
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">{t('locationHealth.needsAttention')}</p>
                                <p className="text-3xl font-bold text-amber-600">{data.summary.needs_attention || 0}</p>
                            </div>
                            <AlertTriangle className="w-10 h-10 text-amber-200" />
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">{t('locationHealth.needsAttentionHint')}</p>
                    </CardContent>
                </Card>

                <Card className="border-l-4 border-l-red-500">
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">{t('locationHealth.critical')}</p>
                                <p className="text-3xl font-bold text-red-600">{data.summary.critical || 0}</p>
                            </div>
                            <AlertCircle className="w-10 h-10 text-red-200" />
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">{t('locationHealth.criticalHint')}</p>
                    </CardContent>
                </Card>

                <Card className="border-l-4 border-l-blue-500">
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">{t('locationHealth.incrementalROI')}</p>
                                <p className="text-3xl font-bold text-blue-600">
                                    ${incrementalROI?.total_roi_value?.toLocaleString() || 0}
                                </p>
                            </div>
                            <TrendingUp className="w-10 h-10 text-blue-200" />
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                            {t('locationHealth.reviewsGenerated', { count: incrementalROI?.total_incremental_reviews || 0 })}
                        </p>
                    </CardContent>
                </Card>
            </div>

            {/* Filters */}
            <Card>
                <CardContent className="pt-6">
                    <div className="flex flex-wrap gap-4">
                        {isSuperAdmin() && (
                            <div className="min-w-[200px]">
                                <Label className="text-sm text-muted-foreground mb-1 block">{t('locationHealth.organization')}</Label>
                                <Select value={selectedOrg || '__all__'} onValueChange={(v) => setSelectedOrg(v === '__all__' ? '' : v)}>
                                    <SelectTrigger>
                                        <Building2 className="w-4 h-4 mr-2 text-muted-foreground" />
                                        <SelectValue placeholder={t('locationHealth.allOrganizations')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__all__">{t('locationHealth.allOrganizations')}</SelectItem>
                                        {organizations.map(org => (
                                            <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        
                        <div className="min-w-[200px]">
                            <Label className="text-sm text-muted-foreground mb-1 block">
                                {t('locationHealth.statusFilter')}
                                <HelpTooltip text={t('locationHealth.statusFilterHint')} />
                            </Label>
                            <Select value={statusFilter || '__all__'} onValueChange={(v) => setStatusFilter(v === '__all__' ? '' : v)}>
                                <SelectTrigger>
                                    <Filter className="w-4 h-4 mr-2 text-muted-foreground" />
                                    <SelectValue placeholder={t('locationHealth.allStatuses')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__all__">{t('locationHealth.allStatuses')}</SelectItem>
                                    <SelectItem value="healthy">{t('locationHealth.healthyOnly')}</SelectItem>
                                    <SelectItem value="needs_attention">🟡 {t('locationHealth.needsAttention')}</SelectItem>
                                    <SelectItem value="critical">{t('locationHealth.criticalOnly')}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Locations Table */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <MapPin className="w-5 h-5 text-emerald-500" />
                        {t('locationHealth.locationPerformance')}
                    </CardTitle>
                    <CardDescription>
                        {t('locationHealth.locationPerformanceHint')}
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {data.locations.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                            <MapPin className="w-12 h-12 mx-auto mb-4 opacity-50" />
                            <p>{t('locationHealth.noLocationsFound')}</p>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>{t('locationHealth.locationCol', { defaultValue: 'Location' })}</TableHead>
                                    <TableHead>
                                        {t('locationHealth.healthScore')}
                                        <HelpTooltip text={t('locationHealth.healthScoreHint', { defaultValue: 'Overall performance score (0-100) based on review velocity and staff adoption' })} />
                                    </TableHead>
                                    <TableHead>
                                        {t('locationHealth.reviewVelocity')}
                                        <HelpTooltip text={t('locationHealth.reviewVelocityHint', { defaultValue: 'How many reviews per day this location is generating' })} />
                                    </TableHead>
                                    <TableHead>
                                        {t('locationHealth.staffAdoption')}
                                        <HelpTooltip text={t('locationHealth.staffAdoptionHint', { defaultValue: 'Percentage of employees actively using their cards (at least 1 tap in 30 days)' })} />
                                    </TableHead>
                                    <TableHead>
                                        {t('locationHealth.baselineCol', { defaultValue: 'Baseline' })}
                                        <HelpTooltip text={t('locationHealth.baselineHint', { defaultValue: 'Whether starting values have been set for accurate ROI tracking' })} />
                                    </TableHead>
                                    <TableHead>{t('locationHealth.statusCol', { defaultValue: 'Status' })}</TableHead>
                                    <TableHead></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {data.locations.map(loc => (
                                    <>
                                        <TableRow 
                                            key={loc.location_id}
                                            className="cursor-pointer hover:bg-muted"
                                            onClick={() => setExpandedLocation(expandedLocation === loc.location_id ? null : loc.location_id)}
                                        >
                                            <TableCell className="font-medium">{loc.location_name}</TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <Progress value={loc.overall_score} className="w-20 h-2" />
                                                    <span className="text-sm font-medium">{loc.overall_score}%</span>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <span className="font-medium">{loc.metrics?.review_velocity?.daily || 0}</span>
                                                <span className="text-muted-foreground text-sm">{t('locationHealth.perDay', { defaultValue: '/day' })}</span>
                                            </TableCell>
                                            <TableCell>
                                                <span className="font-medium">{loc.metrics?.staff_adoption?.rate || 0}%</span>
                                                <span className="text-muted-foreground text-sm ml-1">
                                                    ({loc.metrics?.staff_adoption?.active_employees || 0}/{loc.metrics?.staff_adoption?.total_employees || 0})
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                {loc.baseline?.has_baseline ? (
                                                    <Badge variant="outline" className="bg-emerald-50 text-emerald-700">
                                                        <CheckCircle className="w-3 h-3 mr-1" />
                                                        {t('locationHealth.baseline.set')}
                                                    </Badge>
                                                ) : (
                                                    <Badge variant="outline" className="bg-amber-50 text-amber-700">
                                                        <AlertTriangle className="w-3 h-3 mr-1" />
                                                        {t('locationHealth.baseline.notSet')}
                                                    </Badge>
                                                )}
                                            </TableCell>
                                            <TableCell>{getStatusBadge(loc.health_status)}</TableCell>
                                            <TableCell>
                                                {expandedLocation === loc.location_id ? (
                                                    <ChevronUp className="w-4 h-4 text-muted-foreground" />
                                                ) : (
                                                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                                                )}
                                            </TableCell>
                                        </TableRow>
                                        {expandedLocation === loc.location_id && (
                                            <TableRow>
                                                <TableCell colSpan={7}>
                                                    <LocationDetailPanel location={loc} />
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Health Thresholds Dialog */}
            <Dialog open={showThresholds} onOpenChange={setShowThresholds}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Target className="w-5 h-5" />
                            {t('locationHealth.thresholdsTitle', { defaultValue: 'Health Score Targets' })}
                        </DialogTitle>
                        <DialogDescription>
                            {t('locationHealth.thresholdsDesc', { defaultValue: 'Customize the thresholds that determine healthy vs. needs attention vs. critical status.' })}
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-6 py-4">
                        <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                                Review Velocity Target (monthly)
                                <HelpTooltip text="How many reviews per month a location should generate to be considered healthy" />
                            </Label>
                            <Input
                                type="number"
                                value={thresholds.review_velocity_target}
                                onChange={(e) => setThresholds({...thresholds, review_velocity_target: parseInt(e.target.value) || 0})}
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                                Staff Adoption Target (%)
                                <HelpTooltip text="What percentage of employees should be actively using their cards" />
                            </Label>
                            <Input
                                type="number"
                                value={thresholds.adoption_rate_target}
                                onChange={(e) => setThresholds({...thresholds, adoption_rate_target: parseInt(e.target.value) || 0})}
                            />
                        </div>
                        
                        <div className="border-t pt-4">
                            <p className="text-sm font-medium mb-3">{t('locationHealth.healthStatusThresholds')}</p>
                            
                            <div className="space-y-3">
                                <div className="flex items-center gap-3">
                                    <div className="w-4 h-4 rounded-full bg-emerald-500" />
                                    <span className="text-sm w-24">{t('locationHealth.healthy')}</span>
                                    <span className="text-muted-foreground">≥</span>
                                    <Input
                                        type="number"
                                        className="w-20"
                                        value={thresholds.green_threshold}
                                        onChange={(e) => setThresholds({...thresholds, green_threshold: parseInt(e.target.value) || 0})}
                                    />
                                    <span className="text-sm text-muted-foreground">%</span>
                                </div>
                                
                                <div className="flex items-center gap-3">
                                    <div className="w-4 h-4 rounded-full bg-amber-500" />
                                    <span className="text-sm w-24">{t('locationHealth.attention')}</span>
                                    <span className="text-muted-foreground">≥</span>
                                    <Input
                                        type="number"
                                        className="w-20"
                                        value={thresholds.yellow_threshold}
                                        onChange={(e) => setThresholds({...thresholds, yellow_threshold: parseInt(e.target.value) || 0})}
                                    />
                                    <span className="text-sm text-muted-foreground">%</span>
                                </div>
                                
                                <div className="flex items-center gap-3">
                                    <div className="w-4 h-4 rounded-full bg-red-500" />
                                    <span className="text-sm w-24">{t('locationHealth.critical')}</span>
                                    <span className="text-muted-foreground">&lt;</span>
                                    <span className="text-sm">{thresholds.yellow_threshold}%</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowThresholds(false)}>
                            Cancel
                        </Button>
                        <Button onClick={saveThresholds} disabled={savingThresholds}>
                            {savingThresholds ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                            Save Targets
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// Location Detail Panel Component
function LocationDetailPanel({ location }) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState(null);

    useEffect(() => {
        loadDetail();
    }, [location.location_id]);

    const loadDetail = async () => {
        try {
            const res = await locationHealthAPI.getDetail(location.location_id);
            setDetail(res.data);
        } catch (error) {
            console.error('Failed to load detail:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
            </div>
        );
    }

    return (
        <div className="bg-muted rounded-lg p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Review Velocity Card */}
                <Card>
                    <CardContent className="pt-4">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-muted-foreground">{t('locationHealth.reviewVelocity')}</span>
                            <BarChart3 className="w-4 h-4 text-muted-foreground" />
                        </div>
                        <p className="text-2xl font-bold">{detail?.metrics?.review_velocity?.daily || 0}/day</p>
                        <p className="text-sm text-muted-foreground">
                            {detail?.metrics?.review_velocity?.monthly_projected || 0} projected monthly
                        </p>
                        <Progress 
                            value={detail?.metrics?.review_velocity?.score || 0} 
                            className="mt-2 h-1"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                            Target: {detail?.metrics?.review_velocity?.target || 50}/month
                        </p>
                    </CardContent>
                </Card>

                {/* Staff Adoption Card */}
                <Card>
                    <CardContent className="pt-4">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-muted-foreground">{t('locationHealth.staffAdoption')}</span>
                            <Users className="w-4 h-4 text-muted-foreground" />
                        </div>
                        <p className="text-2xl font-bold">{detail?.metrics?.staff_adoption?.rate || 0}%</p>
                        <p className="text-sm text-muted-foreground">
                            {detail?.metrics?.staff_adoption?.active_employees || 0} of {detail?.metrics?.staff_adoption?.total_employees || 0} employees active
                        </p>
                        <Progress 
                            value={detail?.metrics?.staff_adoption?.score || 0} 
                            className="mt-2 h-1"
                        />
                        <p className="text-xs text-emerald-600 mt-1">
                            {detail?.metrics?.staff_adoption?.highly_active_rate || 0}% highly active (5+ taps)
                        </p>
                    </CardContent>
                </Card>

                {/* Baseline Status Card */}
                <Card>
                    <CardContent className="pt-4">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-muted-foreground">{t('locationHealth.baselineStatus')}</span>
                            <Calendar className="w-4 h-4 text-muted-foreground" />
                        </div>
                        {detail?.baseline?.has_baseline ? (
                            <>
                                <p className="text-lg font-bold text-emerald-600">{t('locationHealth.trackingActive')}</p>
                                <p className="text-sm text-muted-foreground">
                                    Since {detail?.baseline?.baseline_date}
                                </p>
                                <p className="text-sm mt-2">
                                    Started with {detail?.baseline?.google_reviews_start || 0} Google reviews
                                </p>
                            </>
                        ) : (
                            <>
                                <p className="text-lg font-bold text-amber-600">{t('locationHealth.notConfigured')}</p>
                                <p className="text-sm text-muted-foreground">
                                    Set baseline values to track incremental ROI
                                </p>
                            </>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Trends Chart */}
            {detail?.trends && detail.trends.length > 0 && (
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm">{t('locationHealth.weeklyReviewTrend')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <ResponsiveContainer width="100%" height={150}>
                            <LineChart data={detail.trends}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="week" tick={{ fontSize: 12 }} />
                                <YAxis tick={{ fontSize: 12 }} />
                                <RechartsTooltip />
                                <Line 
                                    type="monotone" 
                                    dataKey="reviews" 
                                    stroke="#10b981" 
                                    strokeWidth={2}
                                    dot={{ fill: '#10b981' }}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
